import React, { useRef, useEffect } from 'react';
import { translate } from '../../Services/translation';
import { fieldValuesEqual } from '../../Services/Configurator.service';
import { keyboardNavigationPress } from '../../Services/Wcag';
import { focusFirstIn } from './configuratorFocus';

const ConfiguratorOptionStep = ({
  option,
  optionIndex,
  isOpen,
  isLocked,
  isPastStep,
  moveFocusOnOpen,
  selectedValue,
  selectedValueTitle,
  totalValuesCount,
  availableValuesCount,
  onToggle,
  onRequestEdit,
  onSelectValue,
  onNextStep,
  checkIsAvailable,
}) => {
  const stepRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (isOpen && stepRef.current) {
      // The button that opened this step unmounts with the previous step, so keyboard focus follows the step.
      if (moveFocusOnOpen) {
        focusFirstIn(listRef.current);
      }
      requestAnimationFrame(() => {
        stepRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    }
  }, [isOpen]);

  // Active article matches none of this question's values - let the user skip past it instead of getting stuck.
  const canSkip = availableValuesCount === 0;
  const isDisabled = !selectedValue && !canSkip;

  const hideNotAvailable = option.renderer === 'RenderAs3ValueBoxesOnlyActiveValues' || option.renderer === 'RenderAsValueBoxesOnlyActiveValues';
  const showAsGrid = option.renderer === 'RenderAs3ValueBoxesOnlyActiveValues' || option.renderer === 'RenderAs3ValueBoxes';

  const showEditButton = selectedValueTitle && !isOpen;
  // A header holding the edit button stays a plain container, so the two controls are never nested.
  const canToggle = !isLocked && !isPastStep && !showEditButton;
  const titleId = `configurator-option-${optionIndex}-title`;

  return (
    <div
      ref={stepRef}
      className={`configurator__option-step ${isLocked ? 'is-locked' : ''}`}
      style={{ opacity: isLocked ? 0.6 : 1 }}
    >
      <div
        className="configurator__option-header"
        onClick={!isLocked ? onToggle : undefined}
        {...(canToggle && {
          role: 'button',
          tabIndex: 0,
          'aria-expanded': isOpen,
          onKeyDown: keyboardNavigationPress(onToggle),
        })}
      >
        <p className="configurator__option-name" id={titleId}>
          {option.title}
        </p>
        {!selectedValueTitle && (
          <p className="configurator__option-availability">
            {availableValuesCount} {translate('brenderup.configurator.count.of')} {totalValuesCount} {translate('brenderup.configurator.count.available')}
          </p>
        )}
        {selectedValueTitle && 
          <p className="configurator__option-selected-value">
            {selectedValueTitle}
          </p>
        }
        {showEditButton && (
          <button
            type="button"
            className="configurator__button-edit"
            onClick={(e) => {
              e.stopPropagation();
              onRequestEdit();
            }}
          >
            {translate('brenderup.configurator.button.edit')}
          </button>
        )}
      </div>
      {isOpen && (
        <div
          ref={listRef}
          className={`configurator__option-list ${showAsGrid ? 'grid-version' : ''}`}
          role="group"
          aria-labelledby={titleId}
        >
          {option.text && (
            <div className="configurator__option-text" dangerouslySetInnerHTML={{ __html: option.text }} />
          )}
          {option.values.map((value, valueIndex) => {
            const isSelected = fieldValuesEqual(value.fieldValue, selectedValue);
            const isAvailable = checkIsAvailable
              ? checkIsAvailable(value.fieldValue)
              : true;

            if (hideNotAvailable && !isAvailable) {
              return null;
            }

            const select = () => {
              if (isAvailable) {
                onSelectValue(optionIndex, option, value);
              }
            };

            return (
              <div
                key={value.fieldValue ?? `index:${valueIndex}`}
                role="button"
                tabIndex={isAvailable ? 0 : -1}
                aria-pressed={isSelected}
                aria-disabled={!isAvailable}
                onClick={select}
                onKeyDown={keyboardNavigationPress(select)}
                className={`configurator__option-item ${showAsGrid ? 'grid-version' : ''} ${!isAvailable ? 'disabled' : ''} ${isSelected ? 'is-selected' : ''}`}
                style={{
                  border: isSelected ? '2px solid #c00' : '1px solid #f4f4f4',
                  cursor: isAvailable ? 'pointer' : 'not-allowed',
                  opacity: isAvailable ? 1 : 0.5,
                }}
              >
                {value.imageUrl && (
                  <img
                    src={value.imageUrl}
                    alt={value.title}
                    className="configurator__option-item-img"
                  />
                )}
                <div className="configurator__option-item-text-wrapper">
                  <p className="configurator__option-item-title">
                    {value.title}
                  </p>
                  {value.text &&
                    <div className="configurator__option-item-description" dangerouslySetInnerHTML={{ __html: value.text }} />
                  }
                </div>
              </div>
            );
          })}
          <div className="configurator__option-next-button-container">
            <button
              type="button"
              className={`configurator__button-next ${isDisabled ? 'is-disabled' : ''}`}
              onClick={onNextStep}
              disabled={isDisabled}
              style={{
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.5 : 1,
                pointerEvents: isDisabled ? 'none' : 'auto',
              }}
            >
              {canSkip
                ? translate('brenderup.configurator.button.skip')
                : translate('brenderup.configurator.button.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfiguratorOptionStep;